// 产品 / 套装的业务口径，逐条照原系统代码移植（只做显示用的计算，不参与任何提交）。
//   包装方案名称、默认方案：src/app/(app)/products/[id]/page.tsx packagingLabel / getDefaultPackaging
//   用量规则、需求量、可配齐套数：src/lib/order-bundle-lines.ts
import { num, qty } from '@/lib/format';
import type { Bundle, BundleItem, PackagingOption, ProductStock, SupplierQuote } from './types';

/* ---------------- 包装方案（箱） ---------------- */

export function packagingLabel(o: PackagingOption, index = 0) {
  return o.packageName || o.packageMethod || o.description || `包装方案 ${index + 1}`;
}

export function defaultPackaging(options: PackagingOption[]) {
  return options.find((o) => o.isDefault) || options[0];
}

export function defaultQuote(o?: PackagingOption): SupplierQuote | undefined {
  return o?.supplierQuotes?.find((q) => q.isDefault) || o?.supplierQuotes?.[0];
}

const has = (v: unknown) => v !== null && v !== undefined && v !== '';

/** 每箱数量：「50 个 / 箱」；没填就不显示 */
export function perPackage(o: PackagingOption) {
  return has(o.unitsPerPackage) ? `${qty(o.unitsPerPackage)} 个 / ${o.packageUnit || '箱'}` : null;
}

/** 整箱外尺寸：「60×40×45 cm」；三项都没填就不显示，缺一项写 — */
export function boxSize(o: PackagingOption) {
  if (!has(o.length) && !has(o.width) && !has(o.height)) return null;
  const d = (v: unknown) => (has(v) ? qty(v) : '—');
  return `${d(o.length)}×${d(o.width)}×${d(o.height)} ${o.dimensionUnit || 'cm'}`;
}

export function withUnit(v: unknown, unit: string) {
  return has(v) ? `${qty(v)} ${unit}` : null;
}

/* ---------------- 库存 ---------------- */

/** 多仓合计：可用 = 现有 − 预占，可以为负 */
export function stockTotals(stocks: ProductStock[]) {
  return stocks.reduce(
    (t, st) => {
      const cur = num(st.currentQuantity);
      const res = num(st.reservedQuantity);
      return {
        current: t.current + cur,
        reserved: t.reserved + res,
        available: t.available + cur - res,
        value: st.inventoryValue === undefined ? t.value : (t.value ?? 0) + num(st.inventoryValue),
      };
    },
    { current: 0, reserved: 0, available: 0, value: undefined as number | undefined },
  );
}

/* ---------------- 套装用量规则 ---------------- */

const EPS = 0.000001;

export const isGroup = (i: Pick<BundleItem, 'quantityMode'>) => i.quantityMode === 'PER_GROUP';

/** 用量规则文案：按套「每套 M 个」，按组「每 N 套 M 个」 */
export function ruleText(i: Pick<BundleItem, 'quantity' | 'quantityMode' | 'setsPerGroup'>) {
  const u = qty(i.quantity);
  return isGroup(i) ? `每 ${qty(i.setsPerGroup)} 套 ${u} 个` : `每套 ${u} 个`;
}

/** 下 sets 套需要多少个：按套 = 套数 × 用量；按组 = ceil(套数 ÷ 每组套数) × 用量（calculateBundleRuleQuantity） */
export function needFor(i: BundleItem, sets: number) {
  const n = Math.max(0, sets);
  const u = Math.max(0, num(i.quantity));
  if (!isGroup(i)) return n * u;
  const g = num(i.setsPerGroup);
  if (!(g > 0)) return 0;
  return Math.ceil(Math.max(0, n - EPS) / g) * u;
}

/** 算式文案：「45 × 2」「ceil(45 ÷ 20) × 1」 */
export function needFormula(i: BundleItem, sets: number) {
  return isGroup(i) ? `ceil(${qty(sets)} ÷ ${qty(i.setsPerGroup)}) × ${qty(i.quantity)}` : `${qty(sets)} × ${qty(i.quantity)}`;
}

/** 一个组件按可用量最多撑几套（getBundleLineSetCapacity）；可用为负按 0 */
export function setCapacity(i: BundleItem, available: number) {
  const u = num(i.quantity);
  if (!(u > 0)) return 0;
  const groups = Math.max(0, Math.floor(available / u + EPS));
  if (!isGroup(i)) return groups;
  const g = num(i.setsPerGroup);
  return g > 0 ? groups * g : 0;
}

export function availableIn(i: BundleItem, warehouseId: string) {
  const st = i.product.stocks?.find((x) => x.warehouseId === warehouseId);
  return st ? num(st.currentQuantity) - num(st.reservedQuantity) : 0;
}

export type BundleFit = { warehouseId: string; warehouseName?: string; sets: number; limit?: BundleItem };

/**
 * 可配齐套数：订单从一个仓发货，所以按「单仓」算 —— 每个仓取各组件能撑的最小套数，卡住的组件记下来。
 * 只是参考，下单不拦负库存（文档 23 §11）。组件都没有库存记录时返回空数组。
 */
export function bundleFits(b: Bundle): BundleFit[] {
  const whs = new Map<string, string | undefined>();
  for (const it of b.items) for (const st of it.product.stocks || []) whs.set(st.warehouseId, st.warehouse ? `${st.warehouse.code} · ${st.warehouse.name}` : undefined);
  const out: BundleFit[] = [];
  for (const [warehouseId, warehouseName] of whs) {
    let sets = Infinity;
    let limit: BundleItem | undefined;
    for (const it of b.items) {
      const c = setCapacity(it, availableIn(it, warehouseId));
      if (c < sets) {
        sets = c;
        limit = it;
      }
    }
    out.push({ warehouseId, warehouseName, sets: Number.isFinite(sets) ? sets : 0, limit });
  }
  return out.sort((a, b2) => b2.sets - a.sets);
}
